import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Subscription } from 'rxjs';
import debounce from 'lodash.debounce';
import DOMPurify from 'dompurify';
import { TextField, InputAdornment, Paper, List, ListItem, ListItemText, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { v4 } from 'uuid';
import { ArrowDropDownIcon } from '@mui/x-date-pickers-pro';
import {
  fetchSearchResults,
  getSearchResultsSubject,
  EntityType,
  SearchResult,
  clearSearchResultItems,
  addNewSearch,
  removeSearch
} from '../../../store/Search';
import { Icon, IconType } from '../../Atoms/Icon';

export const ICON_MAP: { [key: string]: IconType } = {
  customer: 'customerSmall',
  asset: 'assetSmall',
  rpm_account: 'rpmAccountSmall',
  tsd: 'tsdSmall',
  ap: 'apSmall',
  supplier: 'supplierSmall',
  rpm_supplier: 'rpmSupplierSmall',
  ticket: 'ticketSmall',
  provisional_asset: 'provisionalAssetSm'
};

export interface SearchBoxProps {
  handleSelect?: (result: SearchResult) => void;
  handleClearSelect?: () => void;
  searchEntityType?: EntityType;
  filters?: Record<string, string>;
  inputString?: string;
  placeholder?: string;
  fullWidth?: boolean;
  inputFocus?: boolean;
  clearOnSuccess?: boolean;
  disabled?: boolean;
  dropdownZIndex?: number;
  fromModal?: boolean;
  showAllResultsLink?: boolean;
}

export const SearchBox = ({
  handleSelect,
  handleClearSelect,
  searchEntityType,
  filters,
  inputString,
  placeholder,
  fullWidth,
  inputFocus = false,
  clearOnSuccess,
  disabled = false,
  dropdownZIndex = 1300,
  fromModal = false,
  showAllResultsLink = false
}: SearchBoxProps) => {
  const [searchId, setSearchId] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [limit, setLimit] = useState<number>(searchEntityType ? 100 : 10);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const entityTypes =
    searchEntityType ||
    [EntityType.CUSTOMER, EntityType.ASSET, EntityType.ACCOUNT, EntityType.TICKET, EntityType.PROVISIONAL_ASSET].join(
      ','
    );
  const listRef = useRef<HTMLUListElement>(null);

  // Track previous disabled state
  const prevDisabledRef = useRef<boolean>(disabled);

  useEffect(() => {
    if (disabled) {
      if (inputValue !== '' && handleClearSelect) {
        handleClearSelect();
        setInputValue('');
      }
    }
    // If disabled changed from false to true, clear search results
    if (!prevDisabledRef.current && disabled && searchId) {
      clearSearchResultItems(searchId);
      setResults([]);
    }
    prevDisabledRef.current = disabled;
  }, [disabled, searchId]);

  const handleSelectResult = async (result: SearchResult) => {
    if (handleSelect) {
      await handleSelect(result);

      if (!clearOnSuccess) {
        setInputValue(result.title);
      } else {
        setResults([]);
        setInputValue('');
      }

      setShowResults(false);
    } else {
      let path = `/${result.source.replace(/_/g, '-')}`;
      if (result.id) path += `/${result.id}`;
      if (result.source === 'search') {
        path += `?query=${encodeURIComponent(inputValue)}`;
      }
      navigate(path);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
      setShowResults(false);
      setActiveIndex(-1);
    }
  };

  const handleSearch = useCallback(
    debounce(
      (query: string, id: string) => {
        // Only fetch search results if query is truthy, or if only searching one entity type
        if (query || searchEntityType) {
          fetchSearchResults(id, { query, entityTypes, filters, limit });
        } else {
          clearSearchResultItems(id);
        }
      },
      200,
      { leading: false, trailing: true }
    ),
    [entityTypes, filters, limit, searchEntityType]
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputValue(value);
    setActiveIndex(-1);
    setShowResults(true);
    if (value === '' && handleClearSelect) {
      handleClearSelect();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    const trimmedValue = event.clipboardData.getData('text').trim();
    setInputValue(trimmedValue);
    setActiveIndex(-1);
    setShowResults(true);
    if (trimmedValue === '' && handleClearSelect) handleClearSelect();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = event;
    if (showResults && results.length > 0) {
      switch (key) {
        case 'ArrowUp':
        case 'ArrowDown':
          event.preventDefault();

          setActiveIndex((prevIndex) => {
            let newIndex;
            if (event.key === 'ArrowUp') {
              newIndex = prevIndex === -1 ? results.length - 1 : Math.max(prevIndex - 1, 0);
            } else {
              newIndex = prevIndex === results.length - 1 ? 0 : Math.min(prevIndex + 1, results.length - 1);
            }
            if (listRef.current) {
              const listItems = listRef.current.children;
              if (listItems && listItems[newIndex]) {
                (listItems[newIndex] as HTMLElement).scrollIntoView({
                  behavior: 'smooth',
                  block: 'nearest'
                });
              }
            }
            return newIndex;
          });
          break;
        case 'Enter':
          if (activeIndex >= 0 && results.length > 0) handleSelectResult(results[activeIndex]);
          break;
        case 'Escape':
          setShowResults(false);
          setActiveIndex(-1);
          break;
        default:
          break;
      }
    } else {
      if (key === 'ArrowDown' && inputValue.length > 0) {
        setShowResults(true);
        setActiveIndex(0);
      }
    }
  };

  const handleAutoSelect = async () => {
    if (inputString && searchId) {
      setInputValue(inputString);
      const res = await fetchSearchResults(searchId, { query: inputString, entityTypes, filters });
      if (res) handleSelectResult(res?.[0]);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    const id = v4();
    setSearchId(id);
    addNewSearch(id);

    return () => {
      removeSearch(id);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (searchEntityType) setLimit(100);
  }, [searchEntityType]);

  useEffect(() => {
    let searchResultsSub: Subscription | undefined;
    if (searchId) {
      searchResultsSub = getSearchResultsSubject(searchId)?.subscribe((res) => {
        const newResults = res || [];
        if (showAllResultsLink && newResults.length) {
          newResults.unshift({
            id: '',
            title: 'View All Results',
            subTitle: null,
            searchBody: null,
            source: 'search',
            rank: 0,
            matchedValue: ''
          });
        }
        setResults(newResults);
      });
    }
    return () => {
      if (searchResultsSub) searchResultsSub.unsubscribe();
    };
  }, [searchId]);

  useEffect(() => {
    if (searchId) handleSearch(inputValue, searchId);
  }, [inputValue, searchId]);

  useEffect(() => {
    if (results.length > 0 && showResults && activeIndex === -1) setActiveIndex(0);
  }, [results, showResults]);

  useEffect(() => {
    if (inputString && searchId && fromModal) {
      handleAutoSelect();
    }
  }, [inputString, searchId, fromModal]);

  // For searches on one entity, update search results when filters change
  useEffect(() => {
    if (searchId && searchEntityType)
      fetchSearchResults(searchId, { query: inputString || '', entityTypes, filters, limit });
  }, [filters, searchId]);

  const styles = (
    fromModal
      ? {
          position: 'fixed',
          top: wrapperRef.current ? wrapperRef.current.getBoundingClientRect().bottom : undefined,
          left: wrapperRef.current ? wrapperRef.current.getBoundingClientRect().left : undefined,
          width: wrapperRef.current ? wrapperRef.current.offsetWidth : undefined
        }
      : {
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0
        }
  ) as React.CSSProperties;

  return (
    <div
      data-testid="search-box"
      className="relative"
      ref={wrapperRef}>
      <TextField
        autoComplete="off"
        data-testid="search-input"
        type="search"
        value={inputValue}
        placeholder={placeholder || ''}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        variant="outlined"
        fullWidth
        name="search"
        size="small"
        disabled={disabled}
        inputRef={(input) => inputFocus && input && input.focus()}
        slotProps={{
          input: {
            onPaste: handlePaste,
            startAdornment: (
              <InputAdornment
                position="start"
                disablePointerEvents={disabled}>
                <SearchIcon />
              </InputAdornment>
            ),
            ...(!!searchEntityType && {
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ mr: -2 }}
                  disablePointerEvents={disabled}>
                  <IconButton onClick={() => setShowResults(!showResults)}>
                    <ArrowDropDownIcon sx={{ color: disabled ? 'action.disabled' : 'action.active' }} />
                  </IconButton>
                </InputAdornment>
              )
            })
          }
        }}
        sx={{ width: fullWidth ? '100%' : searchEntityType ? '18rem' : '32rem' }}
        onFocus={() => setShowResults(true)}
      />
      {showResults && (
        <Paper
          style={{
            zIndex: dropdownZIndex,
            marginTop: '.2rem',
            maxHeight: '20rem',
            overflowY: 'auto',
            pointerEvents: 'auto',
            ...styles
          }}
          elevation={3}>
          <List
            data-testid="search-result-list"
            ref={listRef}>
            {results.length > 0 ? (
              results.map((result, index) => (
                <ListItem
                  title={result.source}
                  sx={{
                    ':hover': { cursor: 'pointer', backgroundColor: 'action.hover' },
                    gap: 2,
                    alignItems: 'center',
                    backgroundColor: activeIndex === index ? 'action.hover' : 'transparent'
                  }}
                  key={result.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectResult(result);
                  }}>
                  {ICON_MAP[result.source] && (
                    <Icon
                      className="p-2"
                      type={ICON_MAP[result.source]}
                    />
                  )}
                  <div>
                    {searchEntityType == EntityType.RPM_PRODUCT ? (
                      <ListItemText primary={`${result.id} - ${result.title}`} />
                    ) : (
                      <ListItemText primary={result.title} />
                    )}

                    <ListItemText
                      primary={<span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(result.subTitle || '') }} />}
                      secondary={
                        <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(result.searchBody || '') }} />
                      }
                    />
                  </div>

                  {result.source && result.source === 'search' && (
                    <Icon
                      className="p-2 !bg-auto !w-[18px] !h-[18px] -ml-2"
                      type="external"
                    />
                  )}
                </ListItem>
              ))
            ) : (
              <ListItem
                data-testid="no-results-found"
                sx={{ textAlign: 'center' }}
                key="none">
                <ListItemText primary="No results" />
              </ListItem>
            )}
          </List>
        </Paper>
      )}
    </div>
  );
};
